import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from './base-api/api.service';
import {
  AllocationPaymentReport,
  BenefitPayment,
  BenefitPaymentStatus,
  ExpenseOverview,
  MonthlyPaymentRequirement,
  ScheduleBenefitPayment,
  ScholarshipClaimReport,
  SettleBenefitPayment,
} from '../models/expense';

/**
 * Expenses: a student's benefit categories and budget position, the recurring
 * payments administrators settle, and the financial reports.
 */
@Injectable({ providedIn: 'root' })
export class ExpenseApiService {
  private readonly api = inject(ApiService);

  // ---- Student -----------------------------------------------------------

  getMyExpenses(): Observable<ExpenseOverview> {
    return this.api
      .get<{ data: ExpenseOverview }>('expenses/my-expenses')
      .pipe(map((response) => response.data));
  }

  /** Read-only budget position for a specific (typically completed) award. */
  getMyAwardExpenses(awardId: number): Observable<ExpenseOverview> {
    return this.api
      .get<{ data: ExpenseOverview }>(`expenses/my-awards/${awardId}`)
      .pipe(map((response) => response.data));
  }

  // ---- Scheduled benefit payments (administrator) ------------------------

  getPaymentsForReview(
    status?: BenefitPaymentStatus | null,
    scholarshipId?: number | null,
    dueBefore?: string | null,
    search?: string
  ): Observable<BenefitPayment[]> {
    const params = new URLSearchParams();
    if (status) {
      params.set('status', status);
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    if (dueBefore) {
      params.set('dueBefore', dueBefore);
    }
    if (search) {
      params.set('search', search);
    }
    const query = params.toString();

    return this.api
      .get<{ data: BenefitPayment[] }>(`admin/expenses/payments${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  /** Schedules the next (or a specific) period of a recurring benefit. */
  schedulePayment(awardBenefitId: number, request: ScheduleBenefitPayment): Observable<BenefitPayment> {
    return this.api
      .post<{ data: BenefitPayment }>(`admin/expenses/benefits/${awardBenefitId}/payments`, request)
      .pipe(map((response) => response.data));
  }

  settlePayment(id: number, request: SettleBenefitPayment): Observable<BenefitPayment> {
    return this.api
      .post<{ data: BenefitPayment }>(`admin/expenses/payments/${id}/settle`, request)
      .pipe(map((response) => response.data));
  }

  cancelPayment(id: number): Observable<unknown> {
    return this.api.post(`admin/expenses/payments/${id}/cancel`, {});
  }

  // ---- Reports -----------------------------------------------------------

  /** Per scholarship: allocated, used, remaining, approved-unpaid and scheduled. */
  getAllocationReport(cycle?: string | null, scholarshipId?: number | null): Observable<AllocationPaymentReport[]> {
    const params = new URLSearchParams();
    if (cycle) {
      params.set('cycle', cycle);
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    const query = params.toString();

    return this.api
      .get<{ data: AllocationPaymentReport[] }>(`admin/reports/allocations${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  /** What must be paid in a month: scheduled payments plus approved claims. */
  getMonthlyPaymentRequirement(
    year?: number | null,
    month?: number | null,
    scholarshipId?: number | null
  ): Observable<MonthlyPaymentRequirement> {
    const params = new URLSearchParams();
    if (year) {
      params.set('year', String(year));
    }
    if (month) {
      params.set('month', String(month));
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    const query = params.toString();

    return this.api
      .get<{ data: MonthlyPaymentRequirement }>(`admin/reports/require-payment${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  /** Claims submitted per scholarship, with status totals. */
  getClaimReport(cycle?: string | null, scholarshipId?: number | null): Observable<ScholarshipClaimReport[]> {
    const params = new URLSearchParams();
    if (cycle) {
      params.set('cycle', cycle);
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    const query = params.toString();

    return this.api
      .get<{ data: ScholarshipClaimReport[] }>(`admin/reports/claims${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }
}
